import { z } from 'zod';

import type {
  ListShipmentCandidateLinesRequest,
  ListShipmentCandidateLinesResponse,
  ShipmentCandidateLineItem,
} from './list-shipment-candidate-lines.dto.js';
import {
  shipmentAvailabilityReasonValues,
  shipmentAvailabilityStatusValues,
} from './list-shipment-candidate-lines.dto.js';
import { IsoDateTimeSchema, PresentationTypeSchema } from './shipments-types.js';

const nonNegativeNumberSchema = z.number().min(0);
const nonNegativeIntSchema = z.number().int().min(0);
const limitSchema = z.number().int().min(1).max(5000);

const ShipmentCandidateLineItemSchema: z.ZodType<ShipmentCandidateLineItem> = z
  .object({
    lineItemId: z.uuid(),
    purchaseCommittedId: z.uuid(),
    occurredAt: IsoDateTimeSchema,
    materialId: z.uuid(),
    presentationType: PresentationTypeSchema,
    netWeightLbs: nonNegativeNumberSchema,
    pieces: nonNegativeIntSchema.nullable(),
    equivalentLbs: nonNegativeNumberSchema.nullable(),
    lineTotalCents: nonNegativeIntSchema,
    baseRatePerLb: nonNegativeNumberSchema.nullable(),
    qualityDiscountMoneyCents: nonNegativeIntSchema,
    isCrvEligible: z.boolean(),
    isReportedToDoriis: z.boolean(),
    shipmentAvailabilityStatus: z.enum(shipmentAvailabilityStatusValues),
    shipmentAvailabilityReason: z.enum(shipmentAvailabilityReasonValues),
    isAlreadyShipped: z.boolean(),
  })
  .strict();

export const ListShipmentCandidateLinesRequestSchema: z.ZodType<ListShipmentCandidateLinesRequest> =
  z
    .object({
      companyId: z.uuid(),
      rcScopeId: z.uuid(),
      materialId: z.uuid(),
      occurredAtStart: IsoDateTimeSchema,
      occurredAtEnd: IsoDateTimeSchema,
      presentationType: PresentationTypeSchema.optional(),
      includeAlreadyShipped: z.boolean().optional().default(false),
      limit: limitSchema.optional().default(500),
    })
    .strict()
    .superRefine((value, ctx) => {
      const occurredAtStartMs = Date.parse(value.occurredAtStart);
      const occurredAtEndMs = Date.parse(value.occurredAtEnd);
      if (
        !Number.isNaN(occurredAtStartMs) &&
        !Number.isNaN(occurredAtEndMs) &&
        occurredAtStartMs >= occurredAtEndMs
      ) {
        ctx.addIssue({
          code: 'custom',
          message:
            'occurredAtStart (inclusive) must be before occurredAtEnd (exclusive).',
          path: ['occurredAtEnd'],
        });
      }
    });

export const ListShipmentCandidateLinesResponseSchema: z.ZodType<ListShipmentCandidateLinesResponse> =
  z
    .object({
      items: z.array(ShipmentCandidateLineItemSchema),
      meta: z
        .object({
          returned: nonNegativeIntSchema,
          limit: limitSchema,
        })
        .strict(),
    })
    .strict();
